import { RWaveLoader } from '@rgrd/component-library'
import { useEffect, useRef } from 'react'
import { Navigate } from 'react-router'

import { storage } from '~/shared/storage'

import styles from './LogoutRoute.module.css'
import { useRegardAuth } from './useRegardAuth'

/**
 * `/logout` — the **session reset** (see `GLOSSARY.md`).
 *
 * Ends the session *and* clears the suite's own browser-side state, returning the browser to a
 * cold start. This is deliberately wider than the **Log out** the user menu offers, which keeps the
 * **Last-viewed customer** so a returning admin lands on their own hospital rather than on Demo. The
 * reset exists for the session that has no working surface to act from so it discards everything the
 * suite persisted.
 *
 * It runs on mount rather than behind a confirmation, because typing the address is the intent and
 * there is nothing unsaved to lose.
 *
 * Two things about where this sits:
 *   - It is mounted *outside* `RequireAuth`, unlike every other route. Behind that guard an
 *     unauthenticated hit would loop: login, land here, log out, return to the origin, login again. A
 *     route whose job is ending a session cannot sit behind one that starts a session.
 *   - Which also means the session's claims are never resolved here, and the gate below does not need
 *     them: **Can-logout** reads the **EMR session** record rather than a claim (RND-13708).
 *
 * The gate itself: in **EMR-embedded mode** the EHR owns the session through its SMART launch, so there
 * is nothing here to end and ending Auth0's would destroy a session the suite cannot re-establish.
 * Such a hit is sent to the bootstrap instead.
 */
export const LogoutRoute = () => {
  const { canLogout, logout } = useRegardAuth()

  // Guarded on a ref rather than on the effect's dependencies: `main.tsx` mounts the tree in
  // `StrictMode`, which runs a mount's effects setup → cleanup → setup, and a second pass would
  // fire a second redirect.
  const resetRequested = useRef(false)

  useEffect(() => {
    if (!canLogout || resetRequested.current) {
      return
    }

    resetRequested.current = true

    storage.clearAll()
    logout()
  }, [canLogout, logout])

  if (!canLogout) {
    return <Navigate replace to="/" />
  }

  return (
    <div className={styles.loggingOut}>
      <RWaveLoader label="Logging out…" />
    </div>
  )
}
